import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Package, CheckCircle2, Clock, Boxes, Factory, TrendingUp, Target, Percent,
  XCircle, Cog, PauseCircle, AlertTriangle, CalendarClock, Layers, Database,
} from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);
function presetRange(preset) {
  const now = new Date();
  const t = iso(now);
  if (preset === "today") return { from: t, to: t };
  if (preset === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); return { from: iso(y), to: iso(y) }; }
  if (preset === "week") { const s = new Date(now); const dow = (s.getDay() + 6) % 7; s.setDate(s.getDate() - dow); return { from: iso(s), to: t }; }
  if (preset === "month") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return { from: iso(s), to: t }; }
  return { from: t, to: t };
}
const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]];

const AXIS = "hsl(var(--muted-foreground))";
const GRID = "hsl(var(--border))";
const chartColors = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function tipStyle() {
  return {
    contentStyle: {
      background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
      borderRadius: 10, color: "hsl(var(--popover-foreground))", fontSize: 12,
    },
    labelStyle: { color: "hsl(var(--muted-foreground))" },
  };
}

function Kpi({ icon: Icon, label, value, tone = "default", suffix, testid }) {
  const tones = {
    default: "text-foreground", primary: "text-primary", success: "text-success",
    warning: "text-warning", danger: "text-destructive",
  };
  const bg = {
    default: "bg-secondary text-muted-foreground", primary: "bg-primary/12 text-primary",
    success: "bg-success/12 text-success", warning: "bg-warning/12 text-warning",
    danger: "bg-destructive/12 text-destructive",
  };
  return (
    <Card className="kpi-card p-4 flex items-center gap-4" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${bg[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold truncate">{label}</div>
        <div className={`font-num font-bold text-2xl leading-tight ${tones[tone]}`}>
          {value}{suffix}
        </div>
      </div>
    </Card>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">{title}</h3>
      <div style={{ width: "100%", height: 240 }}>{children}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [preset, setPreset] = useState("month");
  const [dept, setDept] = useState("all");
  const [custom, setCustom] = useState(presetRange("month"));
  const range = preset === "custom" ? custom : presetRange(preset);
  const params = { from: range.from, to: range.to, department: dept === "all" ? undefined : dept };
  const { data, isLoading, refetch } = useList("dashboard", "/dashboard", params);
  const { data: departments = [] } = useList("departments", "/departments");
  const k = data?.kpis;
  const c = data?.charts;

  const donutData = useMemo(() => (c?.machine_util || []), [c]);

  const seed = async () => {
    if (!window.confirm("Load demo data? This fills in sample records and never deletes your existing data.")) return;
    try {
      toast.loading("Seeding demo data…", { id: "seed" });
      const res = await api.post("/seed", {});
      toast.success(res.data.note || `Seeded ${res.data.orders} orders, ${res.data.samples} samples`, { id: "seed" });
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed (admin only)", { id: "seed" });
    }
  };

  if (isLoading && !data) return <Loading label="Loading dashboard" />;

  const empty = k && k.total_active_orders === 0 && k.orders_completed === 0 && k.total_order_quantity === 0;

  return (
    <div>
      <PageHeader title="Production Dashboard" subtitle="Live plant overview — synced across all devices" testid="dashboard-header">
        {user?.role === "admin" && (
          <Button variant="outline" onClick={seed} data-testid="seed-btn" className="gap-2">
            <Database className="w-4 h-4" /> Seed Demo Data
          </Button>
        )}
      </PageHeader>

      {/* Date range + department filters */}
      <Card className="p-3 mb-5 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="dashboard-filters">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(([val, label]) => (
            <Button key={val} size="sm" variant={preset === val ? "default" : "outline"}
              onClick={() => setPreset(val)} data-testid={`filter-range-${val}`} className="h-9">
              {label}
            </Button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2" data-testid="filter-custom-range">
            <Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="h-9 w-40" data-testid="filter-from" />
            <span className="text-muted-foreground text-sm">to</span>
            <Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="h-9 w-40" data-testid="filter-to" />
          </div>
        )}
        <div className="lg:ml-auto flex items-center gap-2">
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="h-9 w-52" data-testid="filter-department-select"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground whitespace-nowrap font-num" data-testid="filter-period-label">
            {data?.range ? `${data.range.from} → ${data.range.to} · ${data.range.days}d` : ""}
          </span>
        </div>
      </Card>

      {empty && (
        <Card className="p-6 mb-6 border-dashed text-center">
          <p className="font-semibold">No production data yet.</p>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.role === "admin" ? "Click “Seed Demo Data” to load a realistic factory dataset." : "Ask an administrator to seed demo data."}
          </p>
        </Card>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <Kpi icon={Package} label="Active Orders" value={fmt(k?.total_active_orders)} tone="primary" testid="kpi-active-orders" />
        <Kpi icon={CheckCircle2} label="Completed" value={fmt(k?.orders_completed)} tone="success" testid="kpi-completed" />
        <Kpi icon={Clock} label="Pending" value={fmt(k?.orders_pending)} testid="kpi-pending" />
        <Kpi icon={Boxes} label="Order Qty" value={fmt(k?.total_order_quantity)} testid="kpi-order-qty" />
        <Kpi icon={Factory} label="Produced" value={fmt(k?.total_produced_quantity)} tone="primary" testid="kpi-produced" />
        <Kpi icon={Layers} label="Remaining" value={fmt(k?.remaining_quantity)} tone="warning" testid="kpi-remaining" />
        <Kpi icon={TrendingUp} label="Produced (Period)" value={fmt(k?.todays_production)} tone="success" testid="kpi-today-prod" />
        <Kpi icon={Target} label="Target (Period)" value={fmt(k?.todays_target)} testid="kpi-today-target" />
        <Kpi icon={Percent} label="Efficiency" value={fmt(k?.production_efficiency)} suffix="%" tone="primary" testid="kpi-efficiency" />
        <Kpi icon={XCircle} label="Rejections" value={fmt(k?.rejection_quantity)} tone="danger" testid="kpi-rejections" />
        <Kpi icon={Percent} label="Rejection %" value={fmt(k?.rejection_pct)} suffix="%" tone="danger" testid="kpi-rejection-pct" />
        <Kpi icon={Cog} label="Machines Running" value={fmt(k?.machines_running)} tone="success" testid="kpi-machines-running" />
        <Kpi icon={PauseCircle} label="Machines Idle" value={fmt(k?.machines_idle)} tone="warning" testid="kpi-machines-idle" />
        <Kpi icon={AlertTriangle} label="Delayed Orders" value={fmt(k?.delayed_orders)} tone="danger" testid="kpi-delayed" />
        <Kpi icon={CalendarClock} label="Due Today" value={fmt(k?.orders_due_today)} tone="warning" testid="kpi-due-today" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Target vs Actual Production" testid="chart-target-actual">
          <ResponsiveContainer>
            <BarChart data={c?.target_actual}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="date" stroke={AXIS} fontSize={11} />
              <YAxis stroke={AXIS} fontSize={11} />
              <Tooltip {...tipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="target" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Daily Production Trend" testid="chart-daily-trend">
          <ResponsiveContainer>
            <AreaChart data={c?.daily_trend}>
              <defs>
                <linearGradient id="gprod" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="date" stroke={AXIS} fontSize={11} />
              <YAxis stroke={AXIS} fontSize={11} />
              <Tooltip {...tipStyle()} />
              <Area type="monotone" dataKey="production" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#gprod)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Department-wise Production" testid="chart-dept-wise">
          <ResponsiveContainer>
            <BarChart data={c?.dept_wise} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
              <XAxis type="number" stroke={AXIS} fontSize={11} />
              <YAxis type="category" dataKey="name" stroke={AXIS} fontSize={11} width={80} />
              <Tooltip {...tipStyle()} />
              <Bar dataKey="value" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Rejection Trend" testid="chart-rejection-trend">
          <ResponsiveContainer>
            <LineChart data={c?.rejection_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="date" stroke={AXIS} fontSize={11} />
              <YAxis stroke={AXIS} fontSize={11} />
              <Tooltip {...tipStyle()} />
              <Line type="monotone" dataKey="rejected" stroke="hsl(var(--chart-5))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Order Completion %" testid="chart-order-completion">
          <ResponsiveContainer>
            <BarChart data={c?.order_completion}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" stroke={AXIS} fontSize={10} angle={-20} textAnchor="end" height={50} />
              <YAxis stroke={AXIS} fontSize={11} domain={[0, 100]} />
              <Tooltip {...tipStyle()} />
              <Bar dataKey="value" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Machine Utilization" testid="chart-machine-util">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3}>
                {donutData.map((_, i) => <Cell key={i} fill={chartColors[i % chartColors.length]} />)}
              </Pie>
              <Tooltip {...tipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
